import { FC, useMemo, useState, useEffect, useRef, Key, } from "react";
// 组件
import { ProTooltip } from '../antd';
import SelectInput, { SelectInputActionType } from "./subpage/selectInput";
import SelectDropdown from "./subpage/selectDropdown";
// 方法
import classNames from "classnames";
import useClickOutside from "../Hooks/useClickOutside";
import { getElementScrollLeftAndTop, getElementTopAndLeft } from "../utils/utils";
import { getOptionLabel, getSelectOptions } from "./util";
// 常量
import { DEFAULT_FIELD_NAMES, DropdownOffset, SelectDropdownActionType, DropdownTableOption, DropdownTableValue, DropdownTableProps } from "./interface";
import { TABLE_COLUMN_EMPTY } from "../constants/constant";
import './style.less';

/** 下拉表格 */
const DropdownTable: FC<DropdownTableProps> = props => {
  const {
    value: outValue, options: outOptions, style, className, searchPlaceholder, isDisabledEllipsis, searchDiscriminateCase,
    size, placeholder, showSearch, mode, loading, defaultOpen, defaultValue, allowClear, disabled, actionRef, pagination,
    popupClassName, dropdownStyle, dropdownMatchSelectWidth, listHeight, dropdownFooterRender, fieldNames, isLocalSearch,
    isUpperCase, disabledBorderDashed, disabledRender, columns, dropdownWidth: outDropdownWidth, autoPage, tableRef,
    onClear, onSearch, onBlur, onFocus, onChange, onSelect, getPopupContainer, renderOption, onPaginationChange,
  } = props;
  const [value, setValue] = useState<DropdownTableValue | undefined>(defaultValue); // 下拉框选中的值
  const [options, setOptions] = useState<DropdownTableOption[]>(); // 未过滤的选项
  const [isFocus, setIsFocus] = useState<boolean>(false); // 下拉框是否获取焦点
  const selectInputRef = useRef<SelectInputActionType>(); // input 输入框
  const selectDropdownRef = useRef<SelectDropdownActionType>(); // dropdown
  const selectRef = useRef<HTMLDivElement>(null); // 下拉框节点
  const dropdownRef = useRef<HTMLDivElement>(null); // dropdown节点
  const dropdownOffset = useRef<DropdownOffset>(); // dropdown定位数据
  const isFirst = useRef<boolean>(false); // 是否已经打开过下拉框
  const isChange = useRef<boolean>(false); // 是否触发onChange回调
  const dropdownWidth = useRef<number>(); // dropdown宽度
  useClickOutside([selectRef, dropdownRef], isFocus, () => {
    setIsFocus(false);
    onBlur?.();
  });
  useEffect(() => {
    if (defaultOpen) {
      setTimeout(() => {
        isFirst.current = true;
        handleSetOffset();
        setIsFocus(true);
        onFocus?.();
      }, 200);
    }
  }, []);
  useEffect(() => {
    if (outOptions) {
      setOptions(outOptions);
    }
  }, [outOptions]);
  useEffect(() => {
    isChange.current = false;
    const outValueType: boolean = Array.isArray(outValue) || outValue === undefined;
    if (mode && !outValueType) {
      setValue(undefined);
      return;
    }
    if (!mode && outValueType) {
      setValue(undefined);
      return;
    }
    if (outValue === undefined || outValue === null) {
      setValue(defaultValue);
      return;
    }
    setValue(outValue);
  }, [outValue]);
  useEffect(() => {
    isChange.current && onChange?.(value, getSelectOptions(options, value, mode, fieldNames));
  }, [value]);
  /** 获取下拉框定位 */
  const handleSetOffset = () => {
    const selectElement = selectRef.current;
    const popupContainerOffset: DropdownOffset = {left: 0, top: 0};
    if (getPopupContainer) {
      const popupContainer: HTMLElement = getPopupContainer();
      const popupOffset = getElementTopAndLeft(popupContainer);
      popupContainerOffset.left = popupOffset.left;
      popupContainerOffset.top = popupOffset.top;
    }
    if (selectElement) {
      const selectHeight = selectElement.clientHeight;
      const selectWidth = selectElement.clientWidth;
      const selectOffset = getElementTopAndLeft(selectElement);
      const selectScroll = getElementScrollLeftAndTop(selectElement);
      const pageHeight = document.body.scrollHeight;
      const pageWidth = document.body.scrollWidth;
      dropdownWidth.current = Math.max(selectElement.clientWidth, outDropdownWidth || 0);
      let dropdownLeft = selectOffset.left - popupContainerOffset.left - selectScroll.left;
      let dropdownTop = selectOffset.top + selectHeight - popupContainerOffset.top - selectScroll.top;
      let dropDownBottom = 0;
      if ((dropdownLeft + dropdownWidth.current) > pageWidth) {
        dropdownLeft = dropdownLeft - dropdownWidth.current + selectWidth;
      }
      if ((dropdownTop + (listHeight || 300)) > pageHeight) {
        dropDownBottom = pageHeight - dropdownTop + selectHeight;
        dropdownTop = 0;
      }
      dropdownOffset.current = {
        left: dropdownLeft,
        top: dropdownTop,
        bottom: dropDownBottom,
      };
    }
  };
  /** 点击下拉框 */
  const handleClickSelect = () => {
    if (disabled) return;
    handleSetOffset();
    setIsFocus(prev => {
      const newPrev = !prev;
      if (newPrev && !isFirst.current) {
        isFirst.current = true;
      }
      newPrev ? onFocus?.() : onBlur?.();
      return newPrev;
    });
    // onFocus?.()
  };
  /** 清空搜索框的值 */
  const handleEmptySearch = () => {
    selectDropdownRef.current?.onEmptySearch();
  };
  /** 点击选项 */
  const handleClickItem = (optionValue: Key) => {
    isChange.current = true;
    if (mode === 'multiple' && typeof optionValue !== 'boolean') {
      const newValue = new Set(value as Key[]);
      if (newValue.has(optionValue)) {
        newValue.delete(optionValue);
      } else {
        newValue.add(optionValue);
      }
      const nextValue = newValue.size ? [...newValue] : undefined;
      setValue(nextValue);
      onSelect?.(nextValue, getSelectOptions(options, nextValue, mode, fieldNames));
      return;
    }
    if (mode === 'tags') {
      return;
    }
    if (value !== optionValue) {
      setValue(optionValue);
      onSelect?.(optionValue, getSelectOptions(options, optionValue, mode, fieldNames));
    }
    setIsFocus(false);
    onBlur?.();
  };
  /** 批量改变选中选项 */
  const handleChangeItems = (optionValue: Key[]) => {
    const newValue = new Set(value as Key[]);
    optionValue.forEach(item => {
      if (newValue.has(item)) {
        newValue.delete(item);
      } else {
        newValue.add(item);
      }
    });
    const nextValue = newValue.size ? [...newValue] : undefined;
    setValue(nextValue);
    onSelect?.(nextValue, getSelectOptions(options, nextValue, mode, fieldNames));
  };
  /** 清除选项 */
  const handleClear = () => {
    isChange.current = true;
    setValue(undefined);
    onClear?.();
  };
  /** 获取焦点 */
  const handleFocus = () => {
    setIsFocus(true);
  }
  /** 失去焦点 */
  const handleBlur = () => {
    setIsFocus(false);
  }
  /** 禁用显示 */
  const detailRender = () => {
    if (disabledRender) return disabledRender;
    const disabledRenderNode  = getOptionLabel(options, value, fieldNames);
    if (Array.isArray(disabledRenderNode)) {
      const joinRender = disabledRenderNode.join('，');
      return isDisabledEllipsis ? (
        <ProTooltip title={joinRender} placement="topLeft">{joinRender}</ProTooltip>
      ) : joinRender;
    }
    return disabledRenderNode || TABLE_COLUMN_EMPTY;
  };
  const selectClasses = useMemo(() => {
    return classNames('yb-select', `yb-select-${size}`, className, {
      'yb-select-focus': isFocus,
      'yb-select-disabled': disabled,
    });
  }, [size, isFocus, disabled]);
  if (actionRef) {
    actionRef.current = {
      focus: handleFocus,
      blur: handleBlur,
      emptySearch: handleEmptySearch,
      getOption: () => options || [],
    };
  }

  return (
    <>
      {
        disabled ? (
          <div className={isDisabledEllipsis ? 'yb-line-ellipsis-1' : undefined} style={disabledBorderDashed ? { borderBottom: 'dashed 1px #adb5bd' } : undefined}>
            {detailRender()}
          </div>
        ) : (
          <>
            <div
              ref={selectRef}
              style={style}
              className={selectClasses}
              onClick={handleClickSelect}
            >
              <div
                className="yb-select-tags"
                onMouseLeave={selectInputRef.current?.hideInputClose}
                onMouseEnter={selectInputRef.current?.showInputClose}
              >
                <span className="yb-select-tags-item yb-line-ellipsis-1">
                  {
                    (value !== undefined && value !== null) && (
                      <span className="yb-select-tags-item-text">
                        {
                          Array.isArray(value) ? getOptionLabel(options, value[0], fieldNames) : getOptionLabel(options, value, fieldNames)
                        }
                      </span>
                    )
                  }
                </span>
                {
                  mode === 'multiple' && (
                    <span className="yb-select-tags-num">
                      {
                        Array.isArray(value) && value.length > 1 && (
                          <span className="yb-select-tags-num-text">+{value.length - 1}</span>
                        )
                      }
                    </span>
                  )
                }
              </div>
              <SelectInput
                actionRef={selectInputRef}
                value={value}
                placeholder={placeholder}
                isFocus={isFocus}
                loading={loading}
                disabled={disabled}
                allowClear={allowClear}
                onClear={handleClear}
              />
            </div>
            {
              isFirst.current && (
                <SelectDropdown
                  actionRef={selectDropdownRef}
                  tableRef={tableRef}
                  show={isFocus}
                  loading={loading}
                  dropdownRef={dropdownRef}
                  left={dropdownOffset.current?.left}
                  top={dropdownOffset.current?.top}
                  bottom={dropdownOffset.current?.bottom}
                  showSearch={showSearch}
                  options={options}
                  value={value}
                  mode={mode}
                  isLocalSearch={isLocalSearch}
                  fieldNames={fieldNames}
                  minWidth={outDropdownWidth || 450}
                  popupClassName={popupClassName}
                  dropdownStyle={dropdownStyle}
                  listHeight={listHeight}
                  dropdownFooterRender={dropdownFooterRender}
                  searchPlaceholder={searchPlaceholder}
                  searchDiscriminateCase={searchDiscriminateCase}
                  isUpperCase={isUpperCase}
                  columns={columns}
                  pagination={pagination}
                  autoPage={autoPage}
                  renderOption={renderOption}
                  onClickItem={handleClickItem}
                  onChangeItems={handleChangeItems}
                  onSearch={onSearch}
                  getPopupContainer={getPopupContainer}
                  onPaginationChange={onPaginationChange}
                />
              )
            }
          </>
        )
      }
    </>
  );
};

DropdownTable.defaultProps = {
  size: 'small',
  showSearch: true,
  loading: false,
  allowClear: false,
  disabled: false,
  fieldNames: DEFAULT_FIELD_NAMES,
  isLocalSearch: true,
  searchPlaceholder: '搜索内容',
  placeholder: '请选择',
  listHeight: 450,
  isDisabledEllipsis: false,
  disabledBorderDashed: true,
  searchDiscriminateCase: false,
  isUpperCase: false,
};

export default DropdownTable;
